import { createCache } from "async-cache-dedupe";
import { safeStringify } from "./utils.js";
const cache = createCache({
    storage: { type: "memory", options: { invalidation: true } },
});
const cacheManager = {
    activeCacheKeys: new Map(),
    definedCaches: new Set(),
    pendingOperations: new Map(),
    defineCache(name, ttl, method) {
        if (!this.definedCaches.has(name)) {
            cache.define(name, { ttl }, method);
            this.definedCaches.add(name);
            this.activeCacheKeys.set(name, new Set());
        }
    },
    async getCachedResult(name, key) {
        return (await cache.get(name, key));
    },
    async setCachedResult(name, key, result, ttl) {
        await cache.set(name, key, result, ttl);
    },
    async invalidateCacheKeys(name, keysToInvalidate) {
        await Promise.all(keysToInvalidate.map(async (key) => {
            await cache.invalidate(name, key);
            this.activeCacheKeys.get(name)?.delete(key);
        }));
    },
    updateActiveCacheKeys(name, newKeys) {
        const activeKeys = this.activeCacheKeys.get(name) || new Set();
        const keysToInvalidate = [...activeKeys].filter((key) => !newKeys.has(key));
        newKeys.forEach((key) => activeKeys.add(key));
        this.activeCacheKeys.set(name, activeKeys);
        return keysToInvalidate;
    },
    async getOrCreateCachedValue(name, cacheKey, createValue, ttl) {
        const pendingKey = `${name}:${cacheKey}`;
        let pendingOperation = this.pendingOperations.get(pendingKey);
        if (!pendingOperation) {
            pendingOperation = (async () => {
                try {
                    const cachedResult = await this.getCachedResult(name, cacheKey);
                    if (cachedResult !== undefined) {
                        return cachedResult;
                    }
                    const result = await createValue();
                    await this.setCachedResult(name, cacheKey, result, ttl);
                    return result;
                }
                finally {
                    this.pendingOperations.delete(pendingKey);
                }
            })();
            this.pendingOperations.set(pendingKey, pendingOperation);
        }
        return pendingOperation;
    },
};
function Cache({ name, ttl, references }) {
    return function (_Target, _name, descriptor) {
        const originalMethod = descriptor.value;
        cacheManager.defineCache(name, ttl, originalMethod);
        return {
            ...descriptor,
            value: async function (...args) {
                const baseKey = `${name}:${safeStringify(args)}`;
                try {
                    const createValue = () => originalMethod.apply(this, args);
                    if (references) {
                        const initialRefs = await references(args[0], baseKey, {});
                        for (const ref of initialRefs) {
                            try {
                                return await cacheManager.getOrCreateCachedValue(name, ref, createValue, ttl);
                            }
                            catch (error) {
                                console.error(`Error trying reference ${ref}:`, error);
                                continue;
                            }
                        }
                    }
                    return await cacheManager.getOrCreateCachedValue(name, baseKey, createValue, ttl);
                }
                catch (error) {
                    console.error(`Error in cached function ${name}:`, error);
                    return originalMethod.apply(this, args);
                }
            },
        };
    };
}
function InvalidateCache({ name, references }) {
    return function (_Target, _name, descriptor) {
        const originalMethod = descriptor.value;
        return {
            ...descriptor,
            value: async function (...args) {
                const model = await originalMethod.apply(this, args);
                const refs = references(args[0], model);
                await Promise.all([
                    ...(name ? refs.map((ref) => cache.invalidate(name, ref)) : []),
                    ...refs.map((ref) => cache.invalidateAll(ref)),
                ]);
                return model;
            },
        };
    };
}
export { Cache, InvalidateCache };
